// @flow
import * as React from 'react';

import { Outlet, useNavigate } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { selectOrder, type Order, type OrderId } from '@gandi/billing';
import { selectCartIsCommitted, selectCartUUID } from '@gandi/cart';

import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';
import { useTrackingProps } from 'src/hooks/tracking';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { ErrorBoundary } from '../Layout/containers/ErrorBoundary/ErrorBoundary';

import { fetchTrialAndRedirectToConvert } from '../Studio/modules/instances';
import {
  isStudioStepUrl,
  selectIsEligibleStudioUser,
  shouldExcludeFromStudioRedirect,
} from '../modules/gandi-studio';

import { SVGDefs } from './SVGDefs';
import { useAffiliateAttribution } from './useAffiliateAttribution';

type Props = $Exact<{
  ...WithRouterProps<>,
  params: {
    orderId?: OrderId,
  },
}>;

function Root(props: Props): React.Node {
  const navigate = useNavigate();
  const cartIsCommitted: boolean = useAppSelector(selectCartIsCommitted);
  const cartId: ?string = useAppSelector(selectCartUUID);
  const order: ?Order = useAppSelector(state => {
    if (props.params.orderId) {
      return selectOrder(state, {
        orderId: props.params.orderId,
      });
    }
    return null;
  });

  const orderHasToBePaid = order && order.status === 'unpaid' && order.must_pay;
  const redirectToOverview = cartIsCommitted && !orderHasToBePaid;

  React.useEffect(() => {
    // redirect to the overview only if one cart has been found and committed
    // but not if an order has to be paid
    if (redirectToOverview && cartId) {
      navigate(`/cart/${cartId}/overview`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTrackingProps();
  useAffiliateAttribution();

  return (
    <ErrorBoundary>
      <link rel="dns-prefetch" href="//tarteaucitron.io" />
      <link rel="preconnect" href="https://tarteaucitron.io" crossOrigin="" />

      <Outlet />

      <SVGDefs>
        <linearGradient id="gradient">
          <stop stopColor="var(--color-duck-blue-200)" />
          <stop offset="1" stopColor="var(--color-acid-green-600)" />
        </linearGradient>
      </SVGDefs>
    </ErrorBoundary>
  );
}

Root.handleRedirect = async function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  const { dispatch } = args;
  const isStudioUser = selectIsEligibleStudioUser(args.getState());

  if (
    !isStudioUser ||
    isStudioStepUrl(args.req.originalUrl) ||
    shouldExcludeFromStudioRedirect(args.req.originalUrl)
  ) {
    return;
  }

  const protocol = args.req.protocol;
  const host = args.req.get('host');
  const endpoint = `${protocol}://${host}`;

  await dispatch(
    fetchTrialAndRedirectToConvert(
      url => args.res.redirect(`${endpoint}${url}`),
      true,
    ),
  );
};

export default (withRouter(Root): React.ComponentType<{}>);
